/**
 * PostHog bootstrap. The privacy decisions all live in posthogPolicy.js; this
 * file only wires them to the SDK and to Next's router.
 *
 * Safe to call without a key configured: every function becomes a no-op, so
 * the app behaves exactly as it does today until NEXT_PUBLIC_POSTHOG_KEY is
 * set. Nothing here may throw into the app.
 */

import posthog from 'posthog-js';
import { createRepeatGuard } from './eventDedupe';
import {
  groupPath,
  groupUrl,
  isSensitivePath,
  maskAttribute,
  sanitiseEvent,
  shouldMaskText,
  shouldRecord,
} from './posthogPolicy';

const KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

let started = false;

/**
 * The screen of a flow currently on display, for flows whose screens all share
 * one URL. Null whenever the page itself is what the URL names.
 *
 * Every event PostHog sends carries the live `window.location` unless it is
 * told otherwise, so without this only the synthetic pageview knew which
 * sign-up screen it came from: the clicks, the scrolls and the leave event all
 * said /diagnosis. Heatmaps are built from those, so all five screens piled
 * into one overlay. before_send rewrites them to match the pageview.
 */
let screenPath = null;

/**
 * Opt-in class for a single element that must be masked on a page which is
 * otherwise recorded in the clear. Honoured by maskTextFn below.
 */
export const MASK_CLASS = 'ph-mask';

/**
 * Events fired before PostHog finished starting, held until it has.
 *
 * _app.js cannot call initPostHog() until it has read the consent cookie,
 * which costs a state update and so a second render. A page's own effects run
 * during the first commit — and child effects run before the parent's — so any
 * event a page fires on mount arrives while started is still false. That is
 * exactly signup_welcome, the first step of the sign-up funnel and the
 * denominator for every step after it, which was silently dropped on every
 * fresh page load.
 *
 * Nothing is flushed until initPostHog() runs, and that only happens once
 * consent has been accepted, so a visitor who declines leaves this queue to be
 * discarded with the page. Capped so it cannot grow without bound when consent
 * is never given.
 */
const PENDING_LIMIT = 20;
let pending = [];

/**
 * _app.js re-keys the page on every completed route change, so pages remount
 * and their mount effects run again. The flow's own tracking already drops a
 * repeat; PostHog had no such guard, which is why sign-up steps and pageviews
 * arrived in pairs. See utils/eventDedupe.js for why the rule is "nothing was
 * touched in between" rather than "it happened recently" — the pure time
 * window was throwing away a patient who went back a step and forward again.
 */
const isRepeat = createRepeatGuard();

/** Masked stand-in that keeps the shape of the text, so replays stay readable. */
function maskText(text) {
  return String(text ?? '').replace(/\S/g, '*');
}

export function isPostHogEnabled() {
  return Boolean(KEY);
}

export function initPostHog() {
  if (started || !KEY || typeof window === 'undefined') return;

  try {
    posthog.init(KEY, {
      api_host: HOST,
      // Matches the snippet from the PostHog project (627642). It is a
      // snapshot of SDK defaults; every privacy relevant option below is set
      // explicitly and overrides whatever the snapshot would have chosen.
      defaults: '2026-05-30',
      // Pageviews are captured by hand on route change, because Next's client
      // side routing does not reload the page and because the URL has to go
      // through groupPath() first.
      capture_pageview: false,
      capture_pageleave: true,
      // Only build a person profile once someone is actually identified.
      person_profiles: 'identified_only',
      // Off everywhere. It records the text of whatever was clicked, and on
      // this site that text names the condition — live data showed
      // `clicked button with text "Chlamydia"`. See posthogPolicy.js.
      autocapture: false,
      rageclick: false,

      // The `defaults` snapshot above switches these on, which was visible in
      // the network trace: dead-clicks-autocapture.js, surveys.js and
      // web-vitals were all being fetched. Turned off deliberately.
      //
      // Dead clicks record the clicked element the same way autocapture does,
      // so they carry the same risk on patient pages. Exception capture is
      // redundant here because Sentry already does error tracking, and a
      // thrown error can carry form values in its message. Surveys would put
      // a third party prompt in front of patients mid-flow.
      capture_dead_clicks: false,
      capture_exceptions: false,
      disable_surveys: true,
      // Timing only, no element content, so it stays on. URLs still go
      // through the sanitiser.
      capture_performance: true,

      // PostHog silently discards events from anything it thinks is a bot,
      // and a Playwright browser qualifies, so an automated walkthrough of
      // the masking produces an empty project and looks like a broken
      // integration. This opens that filter for local verification only.
      // Unset in production, where the filter should absolutely stay on.
      ...(process.env.NEXT_PUBLIC_POSTHOG_ALLOW_AUTOMATION === '1'
        ? { opt_out_useragent_filter: true }
        : {}),
      disable_session_recording: !shouldRecord(window.location.pathname),

      session_recording: {
        // Replay payloads are partially compressed before they are sent, so a
        // local check of what the recorder actually captured can only read the
        // full DOM snapshot with this off. Verification builds only, behind the
        // same flag as the bot filter above, and never set in production.
        ...(process.env.NEXT_PUBLIC_POSTHOG_ALLOW_AUTOMATION === '1'
          ? { compress_events: false }
          : {}),
        // Every input on every page, which covers the sign-up requirement and
        // is a safe default everywhere else.
        maskAllInputs: true,
        // maskTextSelector decides WHICH text rrweb masks; maskTextFn decides
        // HOW. Without a selector the function is never called at all, which
        // is how "Dental Infection" was still appearing in snapshots. Select
        // everything, then let maskTextFn hand back the original text on the
        // pages that do not need masking.
        maskTextSelector: '*',
        // Evaluated per text node while recording, so the questionnaire,
        // checkout and every identifying page get masked without restarting
        // the recorder.
        // The second argument is the text node's element, which is how an
        // opt-in class is honoured. rrweb's own maskTextClass cannot do it
        // here: maskTextSelector is '*', so this function is what decides,
        // and it would hand back the original text on a page that is not
        // masked wholesale. That is the verification code screen, which
        // prints the patient's email address in the body copy.
        maskTextFn: (text, element) => {
          if (shouldMaskText(window.location.pathname)) return maskText(text);
          try {
            if (element?.closest?.(`.${MASK_CLASS}`)) return maskText(text);
          } catch {
            /* element may be detached or not an Element */
          }
          return text;
        },
        // Text masking covers text nodes only. Attributes are a separate
        // channel and leaked the meta description in full on /lab-testing, so
        // they go through the policy too. NB maskAllElementAttributes must stay
        // off: rrweb ignores this callback when that option is set.
        maskAttributeFn: (name, value) => {
          try {
            return maskAttribute(name, value, window.location.pathname);
          } catch {
            return value;
          }
        },
        // Anything carrying these classes is blocked or masked outright.
        blockClass: 'ph-no-capture',
        maskTextClass: MASK_CLASS,
        // Card fields replay as an empty block rather than a masked one.
        // Targeted by attribute rather than by adding a class to all 18
        // inputs, so a card field added later is covered automatically.
        // These names are consistent across every payment surface:
        // diagnosis checkout, mobile checkout, subscribe checkout,
        // abandoned cart, and both dashboard payment modals.
        blockSelector: [
          '[name="cc-number"]',
          '[name="cc-name"]',
          '[name="expiry-date"]',
          '[name="security-code"]',
          '[autocomplete^="cc-"]',
        ].join(', '),
      },

      before_send: (event) => {
        try {
          return onScreen(sanitiseEvent(event, window.location.pathname));
        } catch {
          // Never let a sanitiser bug leak an unsanitised event.
          return null;
        }
      },

      loaded: () => {
        if (!shouldRecord(window.location.pathname)) {
          posthog.stopSessionRecording?.();
        }
      },
    });

    started = true;

    // opt_out_capturing() persists, so a visitor who ever declined stayed
    // silent forever: accepting later re-ran init but PostHog still had the
    // stored opt-out and dropped everything. init only runs once consent has
    // been given, so clearing it here is exactly the consent the banner took.
    try {
      if (posthog.has_opted_out_capturing?.()) posthog.opt_in_capturing?.();
    } catch {
      /* no-op */
    }

    markSensitiveVisit(window.location.pathname);
    capturePageview(window.location.pathname);
    flushPending();

    // _app.js turns a move into a patient page into a full document load, and
    // announces it on this event before it does. Anything still queued dies
    // with the page otherwise, which is what lost the CTA clicks that lead
    // into the flow.
    window.addEventListener('app:hard-navigation', () => {
      try {
        posthog.shutdown?.();
      } catch {
        /* unloading anyway */
      }
    });
  } catch {
    // Analytics must never take the page down.
  }
}

const SENSITIVE_SEEN_KEY = 'ph_sensitive_seen';

/**
 * Records that this browser has been on a sexual health or lab testing page.
 * sessionStorage rather than a variable so a full page reload still counts.
 */
function markSensitiveVisit(pathname) {
  if (!isSensitivePath(pathname)) return;
  try {
    sessionStorage.setItem(SENSITIVE_SEEN_KEY, '1');
  } catch {
    /* storage blocked: fall through, identify() then simply does not reset */
  }
}

function hasSeenSensitive() {
  try {
    return sessionStorage.getItem(SENSITIVE_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Attaches the person to their account. The internal Parse id and nothing
 * else: no name, email or phone, as agreed with Jose.
 *
 * identify() normally merges everything the visitor did beforehand into the
 * new profile, which would pull their pre-signup sexual health browsing in
 * with it. So if this session has touched one of those pages we reset first,
 * leaving that history behind under the old anonymous id where it can never
 * be tied to a patient.
 *
 * The trade-off, and it is the one Jose accepted: for those visitors the
 * condition-page-to-signup funnel breaks, because their earlier events belong
 * to a different person. For everyone who never opened a sensitive page the
 * funnel is untouched.
 */
export function identifyUser(userId) {
  if (!started || !userId) return;
  try {
    if (hasSeenSensitive()) {
      posthog.reset();
      try {
        sessionStorage.removeItem(SENSITIVE_SEEN_KEY);
      } catch {
        /* no-op */
      }
    }
    posthog.identify(String(userId));
  } catch {
    /* no-op */
  }
}

/** Called on every client side route change. */
export function handleRouteChange(url) {
  if (!started) return;
  try {
    const path = String(url || '').split('?')[0];
    // A real navigation ends whatever flow screen was on display, so the new
    // page reports its own URL rather than inheriting the last screen's.
    clearScreenView();
    markSensitiveVisit(path);
    if (shouldRecord(path)) {
      posthog.startSessionRecording?.();
    } else {
      posthog.stopSessionRecording?.();
    }
    capturePageview(path);
  } catch {
    /* no-op */
  }
}

export function capturePageview(pathname) {
  if (!started) return;
  // A flow screen is already on display and has reported itself. The page's
  // own URL would be a second pageview for the same arrival, which is what a
  // visit opening straight onto a screen produced: the screen's queued view
  // and this one, both rewritten to the screen's path.
  if (screenPath) return;
  try {
    const grouped = groupPath(pathname);
    if (isRepeat(`$pageview|${grouped}`)) return;
    posthog.capture('$pageview', {
      $current_url: groupUrl(window.location.href),
      $pathname: grouped,
    });
  } catch {
    /* no-op */
  }
}

/**
 * A pageview for one screen of a flow that never changes its URL.
 *
 * The sign-up steps all live at /diagnosis and are driven by a `currentStep`
 * state, so PostHog saw a single pageview for the whole sequence and there was
 * no way to watch recordings of, say, the account form on its own. This gives
 * each screen its own path.
 *
 * The path is synthetic, in that nothing is served at it. Everything the
 * screen reports from here on is filed under it, which is what gives each
 * screen its own recordings, funnel row and heatmap rather than one overlay
 * with all five screens' clicks on top of each other.
 *
 * Viewing that heatmap still needs something to load at the path, because the
 * toolbar overlays the page it is on. See pages/diagnosis/step/.
 *
 * @param {string} path e.g. /diagnosis/step/account
 */
export function captureScreenView(path) {
  if (!path || typeof window === 'undefined') return;
  try {
    const grouped = groupPath(path);

    // Recorded before the started check, and not behind it. Which screen is on
    // display is what files every later event, and a visit that opens straight
    // onto a screen renders it before consent has been read and PostHog has
    // started. Leaving it unset there filed that screen's whole session under
    // the page's own URL.
    screenPath = grouped;

    const properties = {
      $current_url: `${window.location.origin}${grouped}`,
      $pathname: grouped,
    };

    if (!started) {
      if (pending.length < PENDING_LIMIT) {
        pending.push({ name: '$pageview', properties, at: new Date() });
      }
      return;
    }

    if (isRepeat(`$pageview|${grouped}`)) return;
    posthog.capture('$pageview', properties);
  } catch {
    /* no-op */
  }
}

/**
 * Back to the page's own URL, for a screen that is what /diagnosis itself
 * renders. Without this the first screen would keep reporting whichever screen
 * the visitor last stepped back from.
 */
export function clearScreenView() {
  screenPath = null;
}

/**
 * Files an event under the screen on display rather than the page's URL.
 *
 * Runs after the sanitiser, so the path it writes is already grouped and
 * nothing it overwrites could have carried a condition out. Only properties
 * the event already has are touched: an event that never named a URL is not
 * given one.
 */
function onScreen(event) {
  if (!event || !screenPath) return event;

  const props = event.properties;
  if (!props) return event;

  const screenUrl = `${window.location.origin}${screenPath}`;

  if (props.$current_url) {
    props.$current_url = screenUrl;
  }
  if (props.$pathname) {
    props.$pathname = screenPath;
  }

  // Heatmap points are filed by the keys of this object, not by $current_url,
  // so rewriting the properties above is not enough on its own: the points
  // would stay under the page's URL and all five screens would overlay each
  // other. The sanitiser has already grouped these keys, so there is at most
  // one of them by now, but they are merged rather than replaced so no points
  // are dropped if that ever stops being true.
  if (props.$heatmap_data && typeof props.$heatmap_data === 'object') {
    const moved = [];
    for (const key of Object.keys(props.$heatmap_data)) {
      const points = props.$heatmap_data[key];
      if (Array.isArray(points)) moved.push(...points);
    }
    props.$heatmap_data = { [screenUrl]: moved };
  }

  return event;
}

/**
 * Fires a named event. Used for the sign-up funnel, the unsupported-state
 * event and the named CTA clicks.
 *
 * Properties are passed through sanitiseEvent like everything else, so a URL
 * in them is grouped and element text is stripped. Keep them small and
 * non-identifying: no names, emails or phone numbers.
 */
export function captureEvent(name, properties = {}) {
  if (!name || typeof window === 'undefined') return;
  if (!started) {
    // Keep the real time of the event, not the time it is eventually sent.
    if (pending.length < PENDING_LIMIT) {
      pending.push({ name: String(name), properties, at: new Date() });
    }
    return;
  }
  try {
    if (isRepeat(signatureFor(name, properties))) return;
    posthog.capture(String(name), properties);
  } catch {
    /* no-op */
  }
}

/** The key a repeat is recognised by. Shared with flushPending. */
function signatureFor(name, properties) {
  try {
    return `${name}|${JSON.stringify(properties || {})}`;
  } catch {
    // A property that will not stringify (a cycle, a BigInt) must not stop
    // the event going out; it just loses the property half of the key.
    return `${name}|?`;
  }
}

/** Sends anything captured before init, oldest first, then empties the queue. */
function flushPending() {
  const queued = pending;
  pending = [];
  for (const item of queued) {
    try {
      // Through the same guard as a live capture, and with the same
      // signature. Flushing straight to posthog.capture left the guard with
      // no record of these, so the remount _app.js causes fired its twin and
      // it looked new — every event a page queues before consent is read went
      // out twice.
      if (isRepeat(signatureFor(item.name, item.properties))) continue;
      posthog.capture(item.name, item.properties, { timestamp: item.at });
    } catch {
      /* one bad event must not block the rest */
    }
  }
}

/** Consent withdrawn, or the visitor never accepted. */
export function stopPostHog() {
  if (!started) return;
  try {
    posthog.stopSessionRecording?.();
    posthog.opt_out_capturing?.();
  } catch {
    /* no-op */
  }
}

export { posthog };
